import React, { useState } from 'react';
import { Box, Typography, Grid, Stack } from '@mui/material';
import { Add, EmojiEvents } from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import CustomButton from '../../../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { createAwardCategory, deleteAwardCategory, updateAwardCategory } from '../../../../redux/contest/actions';
import CreateAwardCategoryDialog from './CreateAwardCategoryDialog';
import AwardCategoryCard from './AwardCategoryCard';
import { IContestManageResponse } from '../../../../redux/contest/types';
import { IAwardCategory } from '../../../../redux/contest/types';
import { TSubmissionCategoryAward } from '../../../../types/global/helper';
import { LaurelCreatorModal } from '../../../LaurelCreator/LaurelCreatorModal';
import { showToastMessage } from '../../../../utils/AppUtils';

interface ContestAwardsTabProps {
    contest: IContestManageResponse['contest'];
    hasSubmissions: boolean;
    awardCategories: IContestManageResponse['award_categories'];
}

export const ContestAwardsTab: React.FC<ContestAwardsTabProps> = ({ contest, hasSubmissions, awardCategories }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedAward, setSelectedAward] = useState<IAwardCategory | null>(null);
    const [isLaurelCreatorOpen, setIsLaurelCreatorOpen] = useState(false);

    // Helper to ensure categories is always an array
    const awardCategoryList: IAwardCategory[] = Array.isArray(awardCategories.categories)
        ? awardCategories.categories
        : awardCategories.categories
        ? [awardCategories.categories]
        : [];

    const handleCreateAward = (awardData: any) => {
        if (!!selectedAward) {
            /** This is edit */
            dispatch(
                updateAwardCategory({
                    documentId: selectedAward._id,
                    payload: awardData,
                }),
            );
        } else {
            // Check if this is a new award creation and if there are existing awards
            if (awardCategoryList.length > 0 && contest.funded) {
                // Calculate total existing award amounts
                const existingTotal = awardCategoryList.reduce((total, award) => {
                    return total + (Number(award.prize) || 0);
                }, 0);

                // Calculate new total with the new award
                const newTotal = existingTotal + (Number(awardData.prize) || 0);

                // Check if new total exceeds available amount
                if (newTotal > (contest.amount_available || 0)) {
                    showToastMessage(
                        'Total award amount exceeds your current fund. Please fund your account first to add more awards.',
                        'error',
                    );
                    return; // Don't create the award
                }
            }

            dispatch(createAwardCategory(awardData));
        }
        setIsCreateDialogOpen(false);
    };

    const handleMoreAction = (action: string, awardId: string) => {
        const award = awardCategoryList.find((a: IAwardCategory) => a._id === awardId);
        if (!award) return;

        switch (action) {
            case 'edit':
                setSelectedAward(award);
                setIsCreateDialogOpen(true);
                break;
            case 'delete':
                // Find the category that contains this award
                const category = contest.categories?.find(
                    (c: any) => Array.isArray(c.category_awards) && c.category_awards.includes(awardId),
                );
                if (category) {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                            payload: {
                                categoryId: category._id,
                                contestId: contest._id,
                            },
                        }),
                    );
                } else {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                        }),
                    );
                }
                break;
            default:
                break;
        }
    };

    if (!contest.categories || contest.categories.length === 0) {
        return (
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].secondaryBg,
                    borderRadius: 2,
                    padding: 4,
                    textAlign: 'center',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '1rem',
                        color: theme[themeStyle].primary_white,
                    }}>
                    Please create submission categories first before creating award categories.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.25rem',
                        color: theme[themeStyle].primary_white,
                    }}>
                    Award Categories
                </Typography>
                <Stack direction="row" spacing={2}>
                    <CustomButton
                        customStyle={{
                            borderRadius: 5,
                        }}
                        buttonProps={{
                            startIcon: <EmojiEvents />,
                            onClick: () => setIsLaurelCreatorOpen(true),
                            sx: {
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                                px: 2,
                                py: 1,
                            },
                        }}
                        label="Create Laurel"
                    />
                    {!contest.winner_email_sent && (
                        <CustomButton
                            customStyle={{
                                borderRadius: 5,
                            }}
                            buttonProps={{
                                startIcon: <Add />,
                                onClick: () => {
                                    setSelectedAward(null);
                                    setIsCreateDialogOpen(true);
                                },
                                sx: {
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].black,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        opacity: 0.9,
                                    },
                                    px: 2,
                                    py: 1,
                                },
                            }}
                            label="Create Award Category"
                        />
                    )}
                </Stack>
            </Box>

            {hasSubmissions && (
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: 2,
                        padding: 2,
                        mb: 3,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].primary_white,
                        }}>
                        Award categories cannot be modified because the campaign already has submissions.
                    </Typography>
                </Box>
            )}

            {!awardCategoryList.length ? (
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: 2,
                        padding: 4,
                        textAlign: 'center',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '1rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        No award categories have been created yet
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {awardCategoryList.map((award: IAwardCategory) => (
                        <Grid item xs={12} md={6} lg={4} key={award._id}>
                            <AwardCategoryCard
                                award={award as unknown as TSubmissionCategoryAward}
                                onMoreAction={handleMoreAction}
                                hasSubmissions={hasSubmissions}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            <CreateAwardCategoryDialog
                open={isCreateDialogOpen}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setSelectedAward(null);
                }}
                onSubmit={handleCreateAward}
                contest={contest}
                editAward={selectedAward}
            />

            <LaurelCreatorModal
                open={isLaurelCreatorOpen}
                onClose={() => setIsLaurelCreatorOpen(false)}
                onSave={laurelData => {
                    console.log('Laurel saved:', laurelData);
                    // TODO: Implement laurel saving logic
                }}
            />
        </Box>
    );
};
